// @ts-nocheck
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import {
  ArrowLeft, RefreshCw, Loader2, Clock, ShieldCheck, AlertTriangle,
  CheckCircle2, ChevronDown, Lock, Send, FileCheck, Check, AlertCircle,
  Info, Lightbulb, BarChart3, Sparkles, XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import ShowcaseRefundReviewPanel from '@/components/admin/ShowcaseRefundReviewPanel';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import { formatTime, formatCalendarDate } from "@/lib/dates";
import { useDivisionColors } from '@/hooks/useDivisionColors';
import { resolveDivisionColors } from '@/lib/divisionPalette';

type Game = {
  id: string;
  team_a_name: string;
  team_b_name: string;
  court_name: string | null;
  scheduled_date: string | null;
  scheduled_start_time: string | null;
  division: string;
};
type ConstraintOutcome = {
  id: string;
  constraint_type: string;
  severity: string | null;
  honored: boolean;
  affected_game_ids: string[];
  reason: string | null;
  details: any;
  team_id: string | null;
};


/**
 * Scheduling-page Tools row: Quality report, Fix issues with AI and Refresh
 * constraint results. Moved unchanged from the retired Schedule review page.
 */
const FN = 'rle-operator-schedule-draft';

export function ScheduleToolsRow({ eventId, versionId, isPublished, onApplied }: {
  eventId: string; versionId: string | null; isPublished: boolean; onApplied?: () => void;
}) {
  const [outcomes, setOutcomes] = useState<ConstraintOutcome[]>([]);
  const [outcomesLoading, setOutcomesLoading] = useState(false);
  const [outcomesShown, setOutcomesShown] = useState(false);
  const [qualityOpen, setQualityOpen] = useState(false);
  const [qualityLoading, setQualityLoading] = useState(false);
  const [qualityData, setQualityData] = useState<any>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiApplying, setAiApplying] = useState(false);
  const [aiData, setAiData] = useState<any>(null);
  const [aiSelected, setAiSelected] = useState<Record<string, boolean>>({});
  const [, setHighlighted] = useState<string[]>([]);
  const busy = null;

  const invoke = async (action: string, extra: Record<string, any> = {}) => {
    const { data: res, error: fnError } = await invokeEdgeFunction(FN, {
      body: { action, event_id: eventId, ...extra },
    });
    if (fnError) return { ok: false, error: { message: fnError.message } } as any;
    return res;
  };

  /** Constraint outcomes are written by assign_slots; read them straight from the table. */
  const loadOutcomes = async (versionId?: string | null) => {
    if (!eventId) return;
    setOutcomesLoading(true);
    try {
      let q = (supabase as any)
        .from('schedule_constraint_outcomes')
        .select('id, constraint_type, severity, honored, affected_game_ids, reason, details, team_id')
        .eq('event_id', eventId);
      if (versionId) q = q.eq('schedule_version_id', versionId);
      const { data: rows, error: qErr } = await q;
      if (qErr) {
        setOutcomes([]);
        return;
      }
      setOutcomes((rows ?? []) as ConstraintOutcome[]);
    } finally {
      setOutcomesLoading(false);
    }
  };

  const doQualityReport = async () => {
    setQualityLoading(true);
    try {
      const res = await invoke('quality_report');
      if (!res?.ok) {
        toast.error(res?.error?.message || 'Could not generate quality report.');
        return;
      }
      setQualityData(res.data?.quality_report ?? null);
      setQualityOpen(true);
    } finally { setQualityLoading(false); }
  };

  const doAiPropose = async () => {
    setAiLoading(true);
    try {
      const res = await invoke('ai_reschedule', { mode: 'propose', min_rest_minutes: 60 });
      if (!res?.ok) {
        toast.error(res?.error?.message || 'Could not generate AI proposal.');
        return;
      }
      const ai = res.data?.ai_reschedule ?? null;
      setAiData(ai);
      const sel: Record<string, boolean> = {};
      (ai?.proposed_moves ?? []).forEach((m: any) => { sel[m.game_id] = true; });
      setAiSelected(sel);
    } finally { setAiLoading(false); }
  };

  const doAiApply = async () => {
    const proposed: any[] = aiData?.proposed_moves ?? [];
    const moves = proposed
      .filter((m) => aiSelected[m.game_id])
      .map((m) => ({
        game_id: m.game_id,
        court_id: m.court_id,
        session_id: m.session_id,
        scheduled_date: m.scheduled_date,
        scheduled_start_time: m.scheduled_start_time,
      }));
    if (moves.length === 0) { toast.error('Select at least one move to apply.'); return; }
    setAiApplying(true);
    try {
      const res = await invoke('ai_reschedule', { mode: 'apply', moves });
      if (!res?.ok) {
        toast.error(res?.error?.message || 'Could not apply moves.');
        return;
      }
      onApplied?.();
      const applied = res.data?.ai_reschedule?.applied_count ?? moves.length;
      toast.success(`Applied ${applied} move${applied === 1 ? '' : 's'}.`);
      setAiData(null);
      setAiSelected({});
      setHighlighted([]);
    } finally { setAiApplying(false); }
  };

  return (
    <div className="mt-4 space-y-3" data-testid="schedule-tools">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-medium text-muted-foreground">Tools</span>
        <Button size="sm" variant="outline" onClick={doQualityReport} disabled={qualityLoading}>
          {qualityLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <BarChart3 className="h-4 w-4" />}
          Quality report
        </Button>
        {!isPublished && (
          <Button size="sm" variant="outline" onClick={doAiPropose} disabled={aiLoading || !!busy}>
            {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Fix issues with AI
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={() => { setOutcomesShown(true); loadOutcomes(versionId); }} disabled={outcomesLoading}>
          {outcomesLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
          Refresh constraint results
        </Button>
      </div>
      {!isPublished && aiData && (
        <Card className="border-primary/40">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              AI reschedule proposal
            </CardTitle>
            {aiData.inputs && (
              <p className="text-xs text-muted-foreground">
                Found {aiData.inputs.rest_issues ?? 0} rest issue
                {(aiData.inputs.rest_issues ?? 0) === 1 ? '' : 's'},{' '}
                {aiData.inputs.violations ?? 0} violation
                {(aiData.inputs.violations ?? 0) === 1 ? '' : 's'}
                {aiData.inputs.min_rest_minutes ? ` · min rest ${aiData.inputs.min_rest_minutes}m` : ''}
              </p>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {aiData.summary && <p className="text-sm">{aiData.summary}</p>}

            {Array.isArray(aiData.proposed_moves) && aiData.proposed_moves.length > 0 ? (
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">
                  Proposed moves ({aiData.proposed_moves.length})
                </p>
                {aiData.proposed_moves.map((m: any) => (
                  <label
                    key={m.game_id}
                    className="flex items-start gap-3 rounded-md border border-border p-3 cursor-pointer hover:bg-accent/40"
                  >
                    <Checkbox
                      className="mt-0.5"
                      checked={!!aiSelected[m.game_id]}
                      onCheckedChange={(v) =>
                        setAiSelected((prev) => ({ ...prev, [m.game_id]: !!v }))
                      }
                    />
                    <div className="min-w-0 flex-1 text-sm">
                      <div className="font-medium">{m.teams}</div>
                      <div className="text-xs text-muted-foreground">
                        → {m.court} · {m.scheduled_date} · {formatTime(m.scheduled_start_time)}
                      </div>
                      {m.reason && <div className="text-xs mt-1">{m.reason}</div>}
                    </div>
                  </label>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No safe moves were proposed.</p>
            )}

            {Array.isArray(aiData.rejected_moves) && aiData.rejected_moves.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">
                  Rejected moves ({aiData.rejected_moves.length})
                </p>
                {aiData.rejected_moves.map((m: any, i: number) => (
                  <div
                    key={`${m.game_id ?? 'r'}-${i}`}
                    className="flex items-start gap-2 rounded-md border border-border p-3 text-sm opacity-60"
                  >
                    <XCircle className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground" />
                    <div className="min-w-0">
                      {m.teams && <div className="font-medium">{m.teams}</div>}
                      <div className="text-xs text-muted-foreground">{m.reason}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {Array.isArray(aiData.warnings) && aiData.warnings.length > 0 && (
              <div className="text-xs">
                <p className="font-medium text-amber-600 dark:text-amber-400 mb-1">Warnings</p>
                <ul className="list-disc pl-5 space-y-0.5 text-muted-foreground">
                  {aiData.warnings.map((w: string, i: number) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}

            {Array.isArray(aiData.unresolvable) && aiData.unresolvable.length > 0 && (
              <div className="text-xs">
                <p className="font-medium text-destructive mb-1">Unresolvable</p>
                <ul className="list-disc pl-5 space-y-0.5 text-muted-foreground">
                  {aiData.unresolvable.map((u: string, i: number) => <li key={i}>{u}</li>)}
                </ul>
              </div>
            )}

            <div className="flex items-center gap-2 pt-1">
              <Button onClick={doAiApply} disabled={aiApplying}>
                {aiApplying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Apply selected moves
              </Button>
              <Button variant="ghost" onClick={() => { setAiData(null); setAiSelected({}); }} disabled={aiApplying}>
                Dismiss
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      {outcomesShown && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Constraints</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {outcomesLoading ? (
                <Skeleton className="h-16 w-full" />
              ) : outcomes.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No constraint outcomes recorded for this draft. They are written when you run "Auto-assign times".
                </p>
              ) : (
                [...outcomes]
                  .sort((a, b) => Number(a.honored) - Number(b.honored))
                  .map((o) => {
                    const notApplied = o.details?.applied_at_placement === false;
                    return (
                      <Collapsible key={o.id} defaultOpen={!o.honored || notApplied}>
                        <div
                          className={cn(
                            'rounded-md border',
                            o.honored && !notApplied
                              ? 'border-green-500/40 bg-green-500/5'
                              : notApplied
                                ? 'border-amber-500/40 bg-amber-500/5'
                                : 'border-destructive/50 bg-destructive/5'
                          )}
                        >
                          <CollapsibleTrigger
                            className="w-full flex items-start gap-2 p-3 text-left"
                            onClick={() => setHighlighted(o.affected_game_ids ?? [])}
                          >
                            {o.honored && !notApplied
                              ? <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                              : notApplied
                                ? <Info className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                                : <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />}
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1 flex-wrap">
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-muted text-muted-foreground">
                                  {o.severity ?? 'constraint'}
                                </span>
                                <span className="text-xs font-medium">{o.constraint_type}</span>
                                {notApplied && (
                                  <span className="text-[10px] text-amber-700 dark:text-amber-400">not applied</span>
                                )}
                              </div>
                            </div>
                            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <div className="px-3 pb-3 pl-9 space-y-1 text-xs text-muted-foreground">
                              {o.reason
                                ? <p>{o.reason}</p>
                                : <p>{o.honored ? 'Honoured in the placed schedule.' : 'No reason recorded.'}</p>}
                              {(o.affected_game_ids ?? []).length > 0 && (
                                <p>{o.affected_game_ids.length} affected game(s)</p>
                              )}
                            </div>
                          </CollapsibleContent>
                        </div>
                      </Collapsible>
                    );
                  })
              )}

            </CardContent>
          </Card>
      )}
      <QualityReportDialog open={qualityOpen} onOpenChange={setQualityOpen} report={qualityData} />
    </div>
  );
}

function gradeColor(grade: string) {
  if (grade === 'A' || grade === 'B') return 'text-green-600 bg-green-50 border-green-200';
  if (grade === 'C' || grade === 'D') return 'text-amber-600 bg-amber-50 border-amber-200';
  return 'text-red-600 bg-red-50 border-red-200';
}

function severityDot(severity: string) {
  if (severity === 'high') return 'bg-red-500';
  if (severity === 'medium') return 'bg-amber-500';
  return 'bg-muted-foreground';
}

function QualityReportDialog({
  open, onOpenChange, report,
}: { open: boolean; onOpenChange: (v: boolean) => void; report: any }) {
  const r = report ?? {};
  const grade = r.grade ?? '-';
  const score = typeof r.score === 'number' ? r.score : '-';
  const issues: { severity: string; description: string }[] = Array.isArray(r.issues) ? r.issues : [];
  const highlights: string[] = Array.isArray(r.highlights) ? r.highlights.filter((h: unknown) => typeof h === 'string') : [];
  const recommendations: string[] = Array.isArray(r.recommendations) ? r.recommendations.filter((rec: unknown) => typeof rec === 'string') : [];
  const stats = r.stats ?? {};
  const numericStats = [
    { label: 'Total games', value: stats.total_games },
    { label: 'Scheduled', value: stats.scheduled },
    { label: 'Unscheduled', value: stats.unscheduled },
    { label: 'Teams evaluated', value: stats.teams_evaluated },
    { label: 'Hard violations', value: stats.hard_violations },
    { label: 'Soft violations', value: stats.soft_violations },
  ].filter((s) => typeof s.value === 'number');
  const courtUtil: { court: string; games: number; pct: number }[] = Array.isArray(stats.court_util) ? stats.court_util : [];
  const criticalRest: { team: string; minRestMin: number }[] = Array.isArray(stats.critical_rest_teams) ? stats.critical_rest_teams : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Schedule quality report
          </DialogTitle>
          <DialogDescription>Review schedule health before publishing.</DialogDescription>
        </DialogHeader>

        {!report ? (
          <p className="text-sm text-muted-foreground">No report data.</p>
        ) : (
          <div className="space-y-5">
            {/* Grade + Score */}
            <div className="flex items-center gap-4">
              <div className={cn('flex h-16 w-16 items-center justify-center rounded-lg border-2 text-3xl font-bold', gradeColor(grade))}>
                {grade}
              </div>
              <div>
                <div className="text-lg font-semibold">{grade} · {score}/100</div>
                <p className="text-sm text-muted-foreground">{r.summary}</p>
              </div>
            </div>

            {/* Stats */}
            {numericStats.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {numericStats.map((s) => (
                  <div key={s.label} className="rounded-md border bg-card p-2 text-center">
                    <div className="text-lg font-semibold">{s.value}</div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{s.label}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Court utilization */}
            {courtUtil.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-2">Court utilization</h4>
                <ul className="flex flex-wrap gap-2">
                  {courtUtil.map((c, i) => (
                    <li key={i} className="rounded-md border bg-card px-2 py-1 text-xs">
                      {c.court}: {c.pct}%
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Critical rest teams */}
            {criticalRest.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-2">Critical rest teams</h4>
                <ul className="flex flex-wrap gap-2">
                  {criticalRest.map((t, i) => (
                    <li key={i} className="rounded-md border bg-card px-2 py-1 text-xs">
                      {t.team} ({t.minRestMin}m)
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Issues */}
            {issues.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
                  <AlertCircle className="h-4 w-4 text-destructive" />
                  Issues ({issues.length})
                </h4>
                <ul className="space-y-1.5">
                  {issues.map((issue, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <span className={cn('mt-1.5 h-2 w-2 rounded-full shrink-0', severityDot(issue?.severity))} />
                      <span>{typeof issue?.description === 'string' ? issue.description : ''}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Highlights */}
            {highlights.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
                  <Check className="h-4 w-4 text-green-600" />
                  Highlights
                </h4>
                <ul className="space-y-1">
                  {highlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Check className="h-3.5 w-3.5 text-green-600 mt-0.5 shrink-0" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Recommendations */}
            {recommendations.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
                  <Lightbulb className="h-4 w-4 text-amber-500" />
                  Recommendations
                </h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
                  {recommendations.map((rec, i) => (
                    <li key={i}>{rec}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ScheduleToolsRow;
